import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, MenuItem, Skeleton, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  clearWorkspaceSettingsError,
  fetchWorkspaceSettings,
  updateWorkspaceSettings,
} from "../../../../../store/features/workspaceSettingsApi";
import SettingsContent from "../../shared/SettingsContent";
import SettingsPageHeader from "../../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../../shared/SettingsPrimaryButton";
import SettingsSecondaryButton from "../../shared/SettingsSecondaryButton";
import { adminLogoPreviewSx, adminSelectSx } from "../../shared/styles";
import AdminFeatureSection from "../AdminFeatureSection";
import AdminLabeledField from "../AdminLabeledField";
import AdminSectionSeparator from "../AdminSectionSeparator";
import WorkingHoursRow from "../WorkingHoursRow";
import WeekendsCheckbox from "../WeekendsCheckbox";
import { ClockIcon, LanguageIcon, WorkspaceIdentityIcon } from "../icons";
import { ReactComponent as DropdownIcon } from "../../../../../assets/images/dropdown-arrow.svg";
import {
  peopleScrollSelectMenuProps,
} from "../../../People/peopleSelectStyles";
import {
  buildDefaultHours,
  buildWorkspaceLogoFormData,
  getErrorMessage,
  LANGUAGE_OPTIONS,
  mapApiToWorkspaceForm,
  mapWorkspaceFormToApi,
  TIMEZONE_OPTIONS,
  WEEKDAYS,
  WEEKEND_DAYS,
} from "../Workspace/constants";

const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_LOGO_SIZE_MB = 5;

const menuItemSx = {
  fontFamily: "Inter",
  fontSize: 14,
};

function WorkspaceSettingsPage() {
  const dispatch = useDispatch();
  const selectMenuProps = peopleScrollSelectMenuProps();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { data, loading, saving, error, statusLoaded } = useSelector(
    (state) => state.workspaceSettings,
  );

  const [workspaceName, setWorkspaceName] = useState("");
  const [primaryWebsite, setPrimaryWebsite] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const fileInputRef = useRef(null);
  const logoPreviewRef = useRef("");
  const [timezone, setTimezone] = useState("UTC");
  const [language, setLanguage] = useState("en");
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [hours, setHours] = useState(buildDefaultHours);

  const timezoneOptions = useMemo(() => {
    if (!timezone || TIMEZONE_OPTIONS.some((opt) => opt.value === timezone)) {
      return TIMEZONE_OPTIONS;
    }
    return [{ value: timezone, label: timezone }, ...TIMEZONE_OPTIONS];
  }, [timezone]);

  const applyFormFromApi = useCallback((apiData) => {
    const form = mapApiToWorkspaceForm(apiData);
    setWorkspaceName(form.workspaceName);
    setPrimaryWebsite(form.primaryWebsite);
    setBusinessEmail(form.businessEmail);
    setBusinessPhone(form.businessPhone);
    setBusinessAddress(form.businessAddress);
    if (!logoFile) {
      setLogoPreview(form.businessLogo);
      logoPreviewRef.current = form.businessLogo;
    }
    setTimezone(form.timezone);
    setLanguage(form.language);
    setIncludeWeekends(form.includeWeekends);
    setHours(form.hours);
  }, [logoFile]);

  useEffect(() => {
    if (!businessId) return;
    dispatch(fetchWorkspaceSettings());
  }, [dispatch, businessId]);

  useEffect(() => {
    if (data) {
      applyFormFromApi(data);
    }
  }, [data, applyFormFromApi]);

  useEffect(() => {
    if (error) {
      toast.error(getErrorMessage(error));
      dispatch(clearWorkspaceSettingsError());
    }
  }, [error, dispatch]);

  const updateDayHours = (day, field, value) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  useEffect(() => {
    return () => {
      if (logoPreviewRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewRef.current);
      }
    };
  }, []);

  const formState = useMemo(
    () => ({
      workspaceName,
      primaryWebsite,
      businessEmail,
      businessPhone,
      businessAddress,
      timezone,
      language,
      includeWeekends,
      hours,
    }),
    [
      workspaceName,
      primaryWebsite,
      businessEmail,
      businessPhone,
      businessAddress,
      timezone,
      language,
      includeWeekends,
      hours,
    ],
  );

  const handleSave = async () => {
    if (!businessId) return;

    const payload = logoFile
      ? buildWorkspaceLogoFormData(logoFile)
      : mapWorkspaceFormToApi(formState);

    const result = await dispatch(updateWorkspaceSettings(payload));
    if (updateWorkspaceSettings.fulfilled.match(result)) {
      if (result.payload?.success === false) {
        toast.error(getErrorMessage(result.payload));
        return;
      }
      if (logoPreviewRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewRef.current);
      }
      setLogoFile(null);
      toast.success(result.payload?.message || "Workspace settings saved.");
      dispatch(fetchWorkspaceSettings());
      return;
    }
    // Rejected updates set `error` in Redux; the error effect toasts once.
  };

  const handleUploadLogo = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, or WEBP image.");
      return;
    }
    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      toast.error(`Logo must be smaller than ${MAX_LOGO_SIZE_MB}MB.`);
      return;
    }

    setLogoLoading(true);
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (logoPreviewRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewRef.current);
      }
      logoPreviewRef.current = objectUrl;
      setLogoPreview(objectUrl);
      setLogoFile(file);
      setLogoLoading(false);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setLogoLoading(false);
      toast.error("Could not load the selected image.");
    };
    img.src = objectUrl;
  };

  const daysToRender = includeWeekends ? [...WEEKDAYS, ...WEEKEND_DAYS] : WEEKDAYS;

  if (!data && (!statusLoaded || loading)) {
    return (
      <SettingsContent>
        <SettingsPageHeader
          title="Workspace Settings"
          description="Configure workspace-wide defaults and preferences"
        />
        <Box sx={{ mt: "32px", display: "flex", flexDirection: "column", gap: "24px" }}>
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rounded" height={240} sx={{ borderRadius: "8px" }} />
        </Box>
      </SettingsContent>
    );
  }

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Workspace Settings"
        description="Configure workspace-wide defaults and preferences"
      />

      <Box sx={{ mt: "32px" }}>
        <AdminFeatureSection
          icon={<WorkspaceIdentityIcon />}
          title="Workspace Identity"
          description="Your workspace branding and information"
        >
          <AdminLabeledField
            label="Workspace Name"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
          />
        </AdminFeatureSection>

        <Box sx={{ mt: "32px" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES.join(",")}
            onChange={handleLogoChange}
            style={{ display: "none" }}
          />
          <AdminFeatureSection
            icon={
              logoLoading ? (
                <CircularProgress size={24} sx={{ color: "#148DFD" }} />
              ) : logoPreview ? (
                <Box
                  component="img"
                  src={logoPreview}
                  alt=""
                  sx={{ width: 40, height: 40, borderRadius: "8px", objectFit: "cover" }}
                />
              ) : (
                <Box component="span" sx={{ fontSize: "18px", fontWeight: 600, color: "#148DFD" }}>
                  TC
                </Box>
              )
            }
            iconSx={adminLogoPreviewSx}
            title="Workspace Logo"
            description="Recommended size: 200x200px, PNG or JPG"
            action={
              <SettingsSecondaryButton
                onClick={handleUploadLogo}
                disabled={saving || logoLoading}
                sx={{ flexShrink: 0 }}
              >
                {logoFile ? "Change Logo" : "Upload Logo"}
              </SettingsSecondaryButton>
            }
          >
            <AdminLabeledField
              label="Primary Website"
              placeholder="Enter your primary website"
              value={primaryWebsite}
              onChange={(e) => setPrimaryWebsite(e.target.value)}
            />
          </AdminFeatureSection>
        </Box>

        <AdminSectionSeparator />

        <AdminFeatureSection
          icon={<ClockIcon />}
          title="Default Timezone"
          description="Applied to all workspace members by default"
        >
          <TextField
            select
            fullWidth
            size="small"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            sx={adminSelectSx}
            SelectProps={{
              IconComponent: DropdownIcon,
              MenuProps: selectMenuProps,
            }}
          >
            {timezoneOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </AdminFeatureSection>

        <AdminSectionSeparator />

        <AdminFeatureSection
          icon={<LanguageIcon />}
          title="Default Language"
          description="Default interface language for the workspace"
        >
          <TextField
            select
            fullWidth
            size="small"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={adminSelectSx}
            SelectProps={{
              IconComponent: DropdownIcon,
              MenuProps: selectMenuProps,
            }}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </AdminFeatureSection>

        <AdminSectionSeparator />

        <AdminFeatureSection
          icon={<ClockIcon />}
          title="Default Working Hours"
          description="Set the default business hours for your workspace"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "32px", width: "100%" }}>
            {daysToRender.map((day) => (
              <WorkingHoursRow
                key={day}
                day={day}
                start={hours[day]?.start || ""}
                end={hours[day]?.end || ""}
                onStartChange={(v) => updateDayHours(day, "start", v)}
                onEndChange={(v) => updateDayHours(day, "end", v)}
              />
            ))}
          </Box>
          <AdminSectionSeparator />
          <WeekendsCheckbox
            checked={includeWeekends}
            onChange={setIncludeWeekends}
            label="Include weekends (Saturday & Sunday)"
          />
        </AdminFeatureSection>

        <SettingsPrimaryButton
          onClick={handleSave}
          disabled={saving || loading || logoLoading}
          sx={{ mt: "40px" }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Workspace Settings"}
        </SettingsPrimaryButton>
      </Box>
    </SettingsContent>
  );
}

export default WorkspaceSettingsPage;
